export const queryKeys = {
  conversations: {
    all: ['conversations'] as const,
    list: () => [...queryKeys.conversations.all, 'list'] as const,
    shared: () => [...queryKeys.conversations.all, 'shared'] as const,
    detail: (id: string) => [...queryKeys.conversations.all, id] as const,
    // realtime-collab-foundation Phase 4 Session 3 — key reserved for future
    // TanStack Query consumers of diff history (e.g., an admin timeline view).
    // The subscriber hook does NOT use this key; it uses plain setInterval
    // over api.getDiffs so it can drain `hasMore` tails without QueryClient
    // re-fetch semantics.
    diffs: (id: string, since: number) =>
      [...queryKeys.conversations.all, id, 'diffs', since] as const,
    /**
     * `GET /api/conversations/:id/model-view` cache key — in-platform-model-view
     * Phase 3. `staleTime: 0` so the Model tab refetches on mount; Phase 4
     * fans Realtime diffs into the local cache directly via the Builder
     * bridge (no full refetch on every cell edit).
     */
    modelView: (id: string) =>
      [...queryKeys.conversations.all, id, 'model-view'] as const,
    /**
     * `GET /api/conversations/:id/views` cache key — builder-ssot-consolidation
     * Phase 5. The unified SSOT projection (inputs/cellValues/modelLayout/signal/
     * dealMetadata/aerialLatLng/meta). Nested under `conversations.all` (via
     * `...all, id, ...`) so `conversations.all` invalidation sweeps it (mirrors
     * modelView). `staleTime:0` on the hook so tabs refetch on mount; Realtime
     * v2 deltas patch this cache granularly (S5.3). ADR 0017.
     */
    views: (id: string) =>
      [...queryKeys.conversations.all, id, 'views'] as const,
    /**
     * `GET /api/conversations/:id/collection-state` cache key —
     * admin-deal-oversight Phase 6. Durable server-read of CollectionState
     * (persisted provenance). Invalidated post-stream so the NEXT banner
     * persists between turns and on load. Nested under `conversations.all`
     * so broad invalidations sweep it automatically.
     */
    collectionState: (id: string) =>
      [...queryKeys.conversations.all, id, 'collection-state'] as const,
    /**
     * `GET /api/conversations/:id/versions` cache key — conversation-model-
     * versioning Phase 5 Session 2. Nested under `conversations.detail(id)` so
     * the Session 8 `conversations.all` invalidation sweeps it automatically
     * (ADR 0020 cascade). `staleVsCurrentModel` is returned per-row by the P2
     * frozen-direct endpoint.
     */
    versions: (convId: string) =>
      [...queryKeys.conversations.detail(convId), 'versions'] as const,
    /**
     * `GET /api/conversations/:id/versions/diff?from=&to=` cache key.
     * `from`/`to` are version refs (`seq | id | 'live'`); default `to='live'`.
     * Nested under `conversations.detail(id)` for the same ADR-0020 sweep.
     */
    versionDiff: (convId: string, from: string, to: string) =>
      [
        ...queryKeys.conversations.detail(convId),
        'versionDiff',
        from,
        to,
      ] as const,
  },
  models: {
    all: ['models'] as const,
    list: () => [...queryKeys.models.all, 'list'] as const,
    /**
     * Admin-all model list key — builder-ux-overhaul Phase 5 F02 fix (2026-05-22).
     * Backed by `GET /api/admin/models` (requireAdmin) which returns ALL latest
     * model families including inactive ones. Scoped separately from `list()` so
     * the admin <ModelList> can show disabled rows without polluting the
     * active-only `list()` used by <NewConversationModal>.
     */
    adminList: () => [...queryKeys.models.all, 'admin-list'] as const,
    detail: (id: string) => [...queryKeys.models.all, id] as const,
    versions: (id: string) =>
      [...queryKeys.models.all, id, 'versions'] as const,
  },
  underwritingEvents: {
    all: ['underwriting-events'] as const,
    list: () => [...queryKeys.underwritingEvents.all, 'list'] as const,
    byConversation: (id: string) =>
      [...queryKeys.underwritingEvents.all, 'conversation', id] as const,
  },
  marketRates: {
    all: ['market-rates'] as const,
    panel: () => [...queryKeys.marketRates.all, 'panel'] as const,
  },
  marketTicker: {
    all: ['market-ticker'] as const,
    payload: () => [...queryKeys.marketTicker.all, 'payload'] as const,
  },
  rateSpreads: {
    all: ['rate-spreads'] as const,
    list: () => [...queryKeys.rateSpreads.all, 'list'] as const,
  },
  /**
   * User profile cache keys (signup-and-billing Phase 1, 2026-05-04).
   * Read path uses the same `/api/admin/me/status` endpoint as
   * `useApprovalStatus` but caches under its own key so the patch mutation
   * has a clean target. Write path: `usePatchUserProfile` invalidates
   * BOTH this key and the `useApprovalStatus` key on success.
   */
  user: {
    all: ['user'] as const,
    profile: () => [...queryKeys.user.all, 'profile'] as const,
  },
  /**
   * Sharing cache keys (`external-sharing` Phase 3 Session 3.1, 2026-05-09).
   * `unreadCount()` is invalidated on inbound `share_link_received` /
   * `share_link_forked_back` Realtime events; `received()` mirrors. `sent()`
   * is the owner-side cache (Sent tab) — invalidated on inbound
   * `share_link_revoked` AND optimistically updated by `useRevokeShare`.
   */
  shares: {
    all: ['shares'] as const,
    sent: () => [...queryKeys.shares.all, 'sent'] as const,
    received: () => [...queryKeys.shares.all, 'received'] as const,
    unreadCount: () => [...queryKeys.shares.all, 'unread-count'] as const,
    detail: (id: string) => [...queryKeys.shares.all, id] as const,
    versionGraph: (shareId: string) =>
      [...queryKeys.shares.all, shareId, 'version-graph'] as const,
    diff: (fromSerialized: string, toSerialized: string) =>
      [...queryKeys.shares.all, 'diff', fromSerialized, toSerialized] as const,
  },
  /**
   * Billing admin cache keys (signup-billing-mvp Phase 5 Session 5, 2026-05-13).
   * `adminTiers()` powers `<TierCatalog>`; `pricingConfig()` powers
   * Session 6's pricing-config editor.
   */
  billing: {
    all: ['billing'] as const,
    adminTiers: () => [...queryKeys.billing.all, 'admin', 'tiers'] as const,
    pricingConfig: () => [...queryKeys.billing.all, 'pricing-config'] as const,
    /**
     * `useBillingUsage` cache key. Phase 5 Session 7 (2026-05-13) — backs
     * the `__root.tsx` redirect cascade (needs `usage_balance` to decide
     * `/billing/checkout` redirect) AND `<CheckoutSuccessPolling>`
     * (refetches every 1s × 10 to detect subscription.created webhook
     * landing). User-scoped so the cache survives sign-in/sign-out.
     */
    usage: (userId: string | undefined) =>
      [...queryKeys.billing.all, 'usage', userId ?? 'anon'] as const,
    /**
     * `GET /api/billing/tiers` cache key — backs `<CheckoutTierGrid>`.
     */
    tiers: () => [...queryKeys.billing.all, 'tiers'] as const,
    /**
     * `useInvoices` cache key — Phase 6 Session 4 (signup-billing-mvp).
     * Backs the Settings → Billing invoice list. User-scoped to match
     * `usage()` so the cache survives sign-in/sign-out.
     */
    invoices: (userId: string | undefined) =>
      [...queryKeys.billing.all, 'invoices', userId ?? 'anon'] as const,
    /**
     * `usePaymentIntent` mutation result cache key — in-app-checkout Phase 3
     * Session 3.3. Keyed by `tierId` so concurrent requests for different
     * tiers don't collide. Not used as a `useQuery` key (the intent is
     * created via `useMutation`), but registered here for invalidation when
     * the subscription is confirmed so a fresh intent is requested if the
     * user retries with a different tier.
     */
    paymentIntent: (tierId: string) =>
      [...queryKeys.billing.all, 'payment-intent', tierId] as const,
    /**
     * `useProrationPreview` cache key — in-app-checkout Phase 3 Session 3.5.
     * Keyed by `targetTierId` so previews for different plan-change targets
     * are cached independently. `staleTime: 0` in the hook (amounts are
     * time-sensitive — the proration date changes every second).
     */
    prorationPreview: (targetTierId: string | undefined) =>
      [
        ...queryKeys.billing.all,
        'proration-preview',
        targetTierId ?? 'none',
      ] as const,
    /**
     * `useSavedCard` cache key — in-app-checkout Phase 3 Session 3.5.
     * User-scoped (`userId`) so the card cache is tied to the authenticated
     * user and cleared on sign-out. Enabled only for Stripe customers.
     */
    savedCard: (userId: string | undefined) =>
      [...queryKeys.billing.all, 'saved-card', userId ?? 'anon'] as const,
    /**
     * `useBespokeRequest` mutation result cache key — in-app-checkout Phase 3
     * Session 3.6. Not used as a `useQuery` key (the request is submitted via
     * `useMutation`), but registered here so the billing module has a
     * consistent namespace and the key is available for manual invalidation if
     * future code needs to reset the bespoke-request state.
     *
     * User-scoped so any cache probe or invalidation is tied to the caller's
     * identity (mirrors the `savedCard` and `usage` key patterns).
     */
    bespokeRequest: (userId: string | undefined) =>
      [...queryKeys.billing.all, 'bespoke-request', userId ?? 'anon'] as const,
  },
};
